<template>
    <div class="ls-additional-details--container">
        <div class="ls-additional-details--heading main">
            <img src="./assets/scope.svg" class="ls-additional-details--icon" />
            <p>Service Details:</p>
        </div>

        <!-- when description is a string -->
        <div v-if="isDescriptionString">
            <p
                id="description"
                test-id="description-string"
                :class="['ls-additional-details--description-string', { 'full-view': showMore }]"
            >
                {{ getAdditionalDetails.description }}
            </p>
            <div v-if="hasImages && showMore">
                <p class="ls-additional-details--heading">Photos</p>
                <LsImageRow :images="getAdditionalDetails.images" test-id="images" />
            </div>
        </div>

        <!-- when description is an array -->
        <div v-else class="description">
            <!-- The wire key is the stable identity; legacy items carry none. -->
            <div
                v-for="(item, idx) in getAdditionalDetails.description"
                :key="item.key || item.title"
                test-id="description"
            >
                <div v-if="!idx" test-id="description-item">
                    <p class="ls-additional-details--heading">{{ item.title }}</p>
                    <p class="ls-additional-details--value">{{ item.value }}</p>
                </div>
                <b-collapse :open="showMore">
                    <div v-if="idx && showMore" test-id="description-item">
                        <p class="ls-additional-details--heading">{{ item.title }}</p>
                        <p class="ls-additional-details--value">{{ item.value }}</p>
                    </div>
                    <div
                        v-if="
                            idx === getAdditionalDetails.description.length - 1 &&
                                showMore &&
                                hasImages
                        "
                    >
                        <p class="ls-additional-details--heading">Photos</p>
                        <LsImageRow :images="getAdditionalDetails.images" test-id="images" />
                    </div>
                </b-collapse>
            </div>
        </div>
        <span
            v-if="showToggle"
            class="ls-additional-details--toggle"
            test-id="toggle"
            @click="toggleShowMore"
        >
            {{ showMore ? 'View Less' : 'View More' }}
        </span>
    </div>
</template>

<script>
import VueTypes from 'vue-types';
import _ from 'lodash';
import LsImageRow from '@/components/LsImageRow/LsImageRow.vue';
import {
    LsAnswerLabels,
    LsItemsToRemoveKey,
    LsOtherItemsKey,
    LsOtherItemValue,
    LsSkippedAnswerKeys,
    LsSlugAnswerValue,
} from './LsAdditionalDetails.constants';

const MANUAL_PRICE = 'manual_price';
const MANUAL_QUOTE = 'manualquote';
const NO_INFORMATION = 'No information';

export default {
    name: 'LsAdditionalDetails',
    components: {
        LsImageRow,
    },
    props: {
        schedule: VueTypes.shape({
            type: VueTypes.string,
            __typename: VueTypes.string.isRequired,
            instant_quote: VueTypes.oneOfType([
                VueTypes.shape({
                    type: VueTypes.string.isRequired,
                }).loose,
                VueTypes.oneOfType([null]),
            ]),
            manualquote: VueTypes.oneOfType([
                VueTypes.shape({
                    description: VueTypes.string,
                    images: VueTypes.array.isRequired,
                }).loose,
                VueTypes.oneOfType([null]),
            ]),
            paw: VueTypes.oneOfType([
                VueTypes.shape({
                    description: VueTypes.string,
                }).loose,
                VueTypes.oneOfType([null]),
            ]),
            qualityassurance: VueTypes.oneOfType([
                VueTypes.shape({
                    description: VueTypes.string,
                }).loose,
                VueTypes.oneOfType([null]),
            ]),
            last_completed_schedule_event: VueTypes.oneOfType([
                VueTypes.shape({
                    images: VueTypes.array,
                }).loose,
                VueTypes.oneOfType([null]),
            ]),
        }).loose.isRequired,
        quote: VueTypes.oneOfType([
            VueTypes.shape({
                answers: VueTypes.oneOfType([VueTypes.object, VueTypes.oneOfType([null])]),
                images: VueTypes.array,
            }).loose,
            VueTypes.oneOfType([null]),
        ]).def(null),
    },
    data() {
        return {
            showMore: false,
            hasMoreDescription: false,
        };
    },
    computed: {
        isDescriptionString() {
            return _.isString(this.getAdditionalDetails.description);
        },
        hasImages() {
            return Boolean(_.size(this.getAdditionalDetails.images));
        },
        showToggle() {
            return (
                (!this.isDescriptionString && this.getAdditionalDetails.description.length > 1) ||
                (this.isDescriptionString && this.hasMoreDescription) ||
                this.hasImages
            );
        },
        isManualQuote() {
            return _.get(this.schedule, 'type') === MANUAL_QUOTE;
        },
        isManualPrice() {
            return (
                !this.isManualQuote && _.get(this.schedule, 'service.price_type') === MANUAL_PRICE
            );
        },
        // LsImageRow wants the legacy snake_case shape with every field a required string.
        manualPriceImages() {
            return _.map(_.get(this.quote, 'images'), (image) => ({
                id: _.toString(image.id),
                title: _.toString(image.title),
                image_url: _.toString(image.imageUrl),
                thumbnail_url: _.toString(image.thumbnailUrl),
            }));
        },
        itemsToRemove() {
            const answers = _.get(this.quote, 'answers');
            const items = this.answerEntries(_.get(answers, LsItemsToRemoveKey));
            const other = _.get(answers, LsOtherItemsKey);
            const hasOther = _.isString(other) && other !== '';

            // The free text details the 'Other' checkbox, so it rides on that entry.
            const otherIndex = hasOther
                ? _.findIndex(items, (item) => _.toLower(item) === LsOtherItemValue)
                : -1;

            const entries = _.map(items, (item, index) =>
                index === otherIndex
                    ? `${this.prettyAnswer(item)}: ${other}`
                    : this.prettyAnswer(item)
            );

            // Append whole: a spread would append the text character by character.
            return hasOther && otherIndex === -1 ? _.concat(entries, other) : entries;
        },
        answerRows() {
            return _.reduce(
                _.get(this.quote, 'answers'),
                (rows, value, key) => {
                    if (_.includes(LsSkippedAnswerKeys, key)) {
                        return rows;
                    }

                    if (key === LsItemsToRemoveKey || key === LsOtherItemsKey) {
                        // One list from two keys, emitted at whichever of them the API sent first.
                        const alreadyEmitted = _.some(rows, { key: LsItemsToRemoveKey });

                        if (alreadyEmitted || _.isEmpty(this.itemsToRemove)) {
                            return rows;
                        }

                        return [
                            ...rows,
                            {
                                key: LsItemsToRemoveKey,
                                title: LsAnswerLabels[LsItemsToRemoveKey],
                                value: this.itemsToRemove.join('\n'),
                            },
                        ];
                    }

                    // Every undisplayable shape reduces to ''.
                    const text = this.answerText(value);

                    if (text === '') {
                        return rows;
                    }

                    // `startCase` alone: lowercasing first destroys the camelCase boundaries.
                    return [
                        ...rows,
                        { key, title: LsAnswerLabels[key] || _.startCase(key), value: text },
                    ];
                },
                []
            );
        },
        getAdditionalDetails() {
            // Must stay first: a manual price's `type` is 'service', so the arms below all miss.
            if (this.isManualPrice) {
                return {
                    images: this.manualPriceImages,
                    description: _.isEmpty(this.answerRows) ? NO_INFORMATION : this.answerRows,
                };
            }

            const type = _.get(this.schedule, 'type') || this.schedule.__typename;

            if (type === 'Recommendation' || type === 'RequiredService') {
                return {
                    images: this.schedule.images,
                    description: this.schedule.description,
                };
            }

            if (type === 'instant_quote') {
                const iqType = this.schedule.instant_quote.type;
                const { images, copy } = this.schedule.instant_quote[iqType];

                return {
                    images,
                    description: copy.description,
                };
            }

            if (type === 'manualquote') {
                const { images, description } = this.schedule.manualquote;

                return {
                    images,
                    description,
                };
            }

            if (type === 'qualityassurance' || type === 'paw') {
                return {
                    description: this.schedule[type].description,
                    images: _.get(this.schedule, 'last_completed_schedule_event.images', []),
                };
            }

            return {
                images: [],
                description: NO_INFORMATION,
            };
        },
    },
    mounted() {
        this.getDescriptionOffsetHeight();
    },
    updated() {
        this.getDescriptionOffsetHeight();
    },
    methods: {
        // `answers` is untyped JSON. `_.join` and `_.filter` silently treat a bare string as a
        // character sequence, so type-guard before iterating.
        answerEntries(value) {
            if (_.isArray(value)) {
                return _.filter(value, _.isString);
            }

            // A lone string is the whole answer, not a list to iterate over.
            return _.isString(value) && value !== '' ? [value] : [];
        },
        answerText(value) {
            return _.join(_.map(this.answerEntries(value), this.prettyAnswer), '\n');
        },
        // Title-case the wire slugs ('full_yard'); leave prose in the customer's own words.
        prettyAnswer(value) {
            return LsSlugAnswerValue.test(value) ? _.startCase(value) : value;
        },
        toggleShowMore() {
            this.showMore = !this.showMore;
        },
        getDescriptionOffsetHeight() {
            const description = document.getElementById('description');
            const maxHeight = 52;

            if (!description) {
                return;
            }
            this.hasMoreDescription = description.offsetHeight >= maxHeight;
        },
    },
};
</script>

<style lang="scss" scoped>
.ls-additional-details--container {
    color: $secondary;
}

.ls-additional-details--icon {
    padding: 0 4px 4px 0;
}

.ls-additional-details--heading {
    font-weight: $semi-bold;
    padding: 5px 0 3px 0;
    &.main {
        display: flex;
        align-items: center;
        padding-bottom: 15px;
        margin-left: -5px;
    }
}

.ls-additional-details--value {
    margin-bottom: 15px;
    white-space: pre-wrap;
}

.ls-additional-details--description-string {
    white-space: pre-line;
    max-height: 54px;
    word-wrap: break-word;
    overflow: hidden;

    &.full-view {
        margin-bottom: 15px;
        max-height: 100%;
    }
}

.ls-additional-details--toggle {
    color: $primary;
    cursor: pointer;
    user-select: none;
}

.ls-additional-details--notes {
    margin-top: 10px;
}
</style>
